import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import LanguageSelector from "../components/LanguageSelector";
import HamburgerMenu from "../components/HamburgerMenu";
import ThemeToggle from "../components/ThemeToggle";
import { signOut } from "aws-amplify/auth";
import { getMyBookings, getReviews, deleteAccount, getBlockedUsers, blockUser } from "../api";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("client");
  const [stats, setStats] = useState({ bookings: 0, reviews: 0, completed: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<Array<{ userId: string; name: string }>>([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [unblockingId, setUnblockingId] = useState("");

  function openBlockedModal() {
    setShowBlockedModal(true);
    setBlockedLoading(true);
    getBlockedUsers()
      .then(setBlockedUsers)
      .catch(() => setBlockedUsers([]))
      .finally(() => setBlockedLoading(false));
  }

  async function handleUnblock(userId: string) {
    setUnblockingId(userId);
    try {
      await blockUser(userId, "unblock");
      setBlockedUsers(prev => prev.filter(u => u.userId !== userId));
    } catch {}
    setUnblockingId("");
  }

  useEffect(() => {
    // Load real booking stats
    getMyBookings().then(data => {
      const bookings = data || [];
      setStats({
        bookings: bookings.length,
        completed: bookings.filter((b: any) => b.status === "completed").length,
        reviews: bookings.filter((b: any) => b.status === "confirmed").length,
      });
    }).catch(() => {});

    const tokenKey = Object.keys(localStorage).find(k => k.includes("idToken"));
    if (tokenKey) {
      const token = localStorage.getItem(tokenKey);
      if (token && token.split(".").length === 3) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setEmail(payload.email || "");
          setName(payload.name || payload.email?.split("@")[0] || "");
          setRole(payload["custom:role"] || "client");
        } catch {}
      }
    }
  }, []);

  async function handleSignOut() {
    await signOut();
    localStorage.clear();
    navigate("/login");
  }

  async function handleDeleteAccount() {
    if (deleteConfirmText !== "DELETE" || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteAccount();
      await signOut();
      localStorage.clear();
      navigate("/");
    } catch (e: any) {
      setDeleteError(e?.response?.data?.error || "Something went wrong. Please try again.");
      setDeleting(false);
    }
  }

  const initials = name
    ? name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
    : email[0]?.toUpperCase() || "U";

  const MENU = [
    { title: "My Account", items: [
      { icon: "📅", label: "My Bookings", path: "/bookings" },
      { icon: "📍", label: "Saved Addresses", path: "/profile/addresses" },
      { icon: "🎫", label: "Support Tickets", path: "/tickets" },
      { icon: "💬", label: "Messages", path: "/messages" },
    ]},
    { title: "Support", items: [
      { icon: "🛡️", label: "Help Center", path: "/support" },
      { icon: "⚠️", label: "Report an Issue", path: "/tickets" },
    ]},
    { title: "Settings", items: [
      { icon: "🚫", label: "Blocked Users", path: "blocked" },
      { icon: "📋", label: "Terms of Service", path: null },
      { icon: "🔒", label: "Privacy Policy", path: null },
    ]},
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-5 pb-4 sticky top-0 z-20 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <HamburgerMenu role={role as any} />
          <h1 className="font-display text-2xl font-bold text-navy">{t("profile")}</h1>
        </div>
      </div>

      <div className="pb-24">
        <div className="bg-white px-5 pt-6 pb-6 mb-3">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-mouna-blue to-navy flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-navy text-lg truncate">{name || email.split("@")[0]}</div>
              <div className="text-xs text-gray-400 truncate">{email}</div>
              <span className="badge badge-blue capitalize mt-1 inline-block">{role}</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Bookings", value: stats.bookings || "0", icon: "📅" },
              { label: "Completed", value: stats.completed || "0", icon: "✅" },
              { label: "Active", value: stats.reviews || "0", icon: "🔄" },
            ].map(stat => (
              <div key={stat.label} className="bg-gray-50 rounded-2xl p-3 text-center">
                <div className="text-xl mb-1">{stat.icon}</div>
                <div className="font-bold text-navy text-sm">{stat.value}</div>
                <div className="text-xs text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {MENU.map(section => (
          <div key={section.title} className="px-5 mb-4">
            <div className="label mb-2">{section.title}</div>
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              {section.items.map((item, i) => (
                <button key={item.label}
                  onClick={() => item.path === "signout" ? handleSignOut() : item.path === "blocked" ? openBlockedModal() : item.path ? navigate(item.path) : null}
                  className={"w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors " +
                    (i < section.items.length - 1 ? "border-b border-gray-100" : "")}>
                  <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="flex-1 font-semibold text-navy text-sm">{item.label}</span>
                  <span className="text-gray-300 text-lg">›</span>
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="px-5 mb-2">
          <div className="label mb-2">Appearance</div>
          <div className="bg-white rounded-2xl shadow-card px-4 py-3 flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center text-lg">🌙</div>
              <span className="font-semibold text-navy text-sm">Dark Mode</span>
            </div>
            <ThemeToggle />
          </div>
        <div className="label mb-2">Language</div>
          <div className="bg-white rounded-2xl shadow-card px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center text-lg">🌍</div>
              <span className="font-semibold text-navy text-sm">App Language</span>
            </div>
            <LanguageSelector />
          </div>
        </div>

        <div className="px-5 mt-4 mb-4">
          <button onClick={handleSignOut}
            className="w-full bg-red-50 text-red-500 rounded-2xl py-4 font-bold text-sm hover:bg-red-100 transition-colors">
            🚪 {t("signOut")}
          </button>
        </div>

        <div className="px-5 mb-4">
          <button onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(""); setDeleteError(""); }}
            className="w-full text-red-400 rounded-2xl py-3 font-semibold text-xs hover:text-red-500 transition-colors">
            Delete my account
          </button>
        </div>

        <div className="text-center text-xs text-gray-300 pb-4">Mouna v1.0</div>
      </div>

      {showBlockedModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-5" onClick={() => setShowBlockedModal(false)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-navy text-lg">Blocked Users</h2>
              <button onClick={() => setShowBlockedModal(false)} className="text-gray-400 text-2xl leading-none">×</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {blockedLoading && (
                <div className="space-y-3 py-2">
                  {[1, 2].map(i => <div key={i} className="skeleton h-12 rounded-xl"></div>)}
                </div>
              )}
              {!blockedLoading && blockedUsers.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-3xl mb-2">🕊️</div>
                  <div className="text-sm text-gray-500">You haven't blocked anyone.</div>
                </div>
              )}
              {!blockedLoading && blockedUsers.map(u => (
                <div key={u.userId} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500 flex-shrink-0">
                    {u.name[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="flex-1 text-sm font-semibold text-navy truncate">{u.name}</span>
                  <button onClick={() => handleUnblock(u.userId)} disabled={unblockingId === u.userId}
                    className="text-xs font-bold text-mouna-blue bg-blue-pale rounded-full px-3 py-1.5 hover:bg-mouna-blue hover:text-white transition-colors disabled:opacity-50">
                    {unblockingId === u.userId ? "..." : "Unblock"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-5">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="text-3xl mb-3 text-center">⚠️</div>
            <h2 className="font-bold text-navy text-lg text-center mb-2">Delete your account?</h2>
            <p className="text-sm text-gray-500 mb-4">
              This permanently deletes your profile, saved addresses, photos, and personal
              data. Your booking history is anonymized for the other party's records.
              This cannot be undone.
            </p>
            <p className="text-xs text-gray-400 mb-2">Type <span className="font-bold text-red-500">DELETE</span> to confirm:</p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoCapitalize="characters"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:border-red-400"
            />
            {deleteError && <p className="text-xs text-red-500 mb-3">{deleteError}</p>}
            <button
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== "DELETE" || deleting}
              className="w-full bg-red-500 text-white rounded-2xl py-3.5 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-600 transition-colors mb-2">
              {deleting ? "Deleting…" : "Permanently delete account"}
            </button>
            <button
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
              className="w-full text-gray-500 rounded-2xl py-3 font-semibold text-sm hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex justify-around py-2 px-4 z-30">
        {[
          { icon: "🏠", label: t("home"), path: "/home" },
          { icon: "🔍", label: t("explore"), path: "/explore" },
          { icon: "📅", label: t("bookings"), path: "/bookings" },
          { icon: "👤", label: t("profile"), path: "/profile" },
        ].map(item => (
          <button key={item.path} onClick={() => navigate(item.path)}
            className="flex flex-col items-center gap-1 px-3 py-1">
            <span className="text-xl">{item.icon}</span>
            <span className={`text-xs font-semibold ${window.location.pathname === item.path ? "text-mouna-blue" : "text-gray-400"}`}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
